/**
 * xlsxHelpers — thin wrapper over `exceljs` for the patterns the app uses.
 *
 * Replaces the `xlsx` (SheetJS) library, which has an unpatched
 * prototype-pollution CVE (GHSA-4r6h-8v6p-xvw6). Every site in this
 * codebase was either:
 *   1. reading a user-supplied .xlsx → first sheet → array-of-arrays
 *      (the `header: 1` sheet_to_json pattern), or
 *   2. building an .xlsx from an array-of-arrays with a fixed column
 *      layout and a single sheet.
 *
 * Both patterns map cleanly to exceljs, so the call sites got a
 * one-liner swap rather than a per-site exceljs rewrite.
 *
 * @module utils/xlsxHelpers
 */

import ExcelJS from "exceljs";

/**
 * Read an .xlsx/.xls ArrayBuffer and return the first sheet as an
 * array-of-arrays (row 0 = header row). Mirrors the shape produced by
 * `XLSX.utils.sheet_to_json(ws, { header: 1 })`.
 *
 * Empty cells come back as `null` (exceljs) rather than `undefined`
 * (SheetJS); callers that did truthy checks (`if (row[i])`) are
 * unaffected. Cells that were formulas are returned as their
 * computed value when the file has cached results — exceljs does not
 * evaluate formulas itself.
 *
 * @param {ArrayBuffer} arrayBuffer - raw file bytes
 * @returns {Promise<Array<Array<*>>>} rows, first row = header
 */
export async function readFirstSheetAoA(arrayBuffer) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);
  const ws = wb.worksheets[0];
  if (!ws) return [];

  const rows = [];
  // exceljs rows are 1-indexed. getSheetValues() returns a sparse
  // array where index 0 is unused; skip it.
  ws.eachRow({ includeEmpty: false }, (row) => {
    // row.values is also 1-indexed with [0] unused.
    const values = row.values.slice(1).map((v) => {
      if (v === undefined || v === null) return null;
      // Cells with rich text / hyperlinks come back as objects.
      if (typeof v === "object") {
        if (Array.isArray(v.richText)) {
          return v.richText.map((t) => t.text).join("");
        }
        if ("text" in v) return v.text;
        if ("result" in v) return v.result; // formula with cached result
        if (v instanceof Date) return v;
      }
      return v;
    });
    rows.push(values);
  });
  return rows;
}

/**
 * Write an array-of-arrays to an .xlsx ArrayBuffer suitable for
 * `new Blob([buffer], { type: "application/vnd..." })`.
 *
 * @param {Array<Array<*>>} aoa - first row is treated as header row
 * @param {object} [opts]
 * @param {string} [opts.sheetName="Sheet1"]
 * @param {Array<{ width?: number }>} [opts.columns] - per-column widths
 * @param {Array<number>} [opts.wrapTextColumns] - 0-indexed cols to wrap
 * @returns {Promise<ArrayBuffer>}
 */
export async function writeAoAToBuffer(aoa, opts = {}) {
  const { sheetName = "Sheet1", columns, wrapTextColumns } = opts;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName);

  aoa.forEach((row) => {
    ws.addRow(row);
  });

  if (Array.isArray(columns)) {
    ws.columns.forEach((col, i) => {
      if (columns[i] && typeof columns[i].width === "number") {
        col.width = columns[i].width;
      }
    });
  }

  if (Array.isArray(wrapTextColumns)) {
    wrapTextColumns.forEach((colIdx) => {
      // exceljs cols are 1-indexed
      const col = ws.getColumn(colIdx + 1);
      if (col) {
        col.eachCell({ includeEmpty: false }, (cell) => {
          cell.alignment = {
            ...(cell.alignment || {}),
            wrapText: true,
            vertical: "top",
          };
        });
      }
    });
  }

  return wb.xlsx.writeBuffer();
}
